import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';

import { Chip } from '@components/base';
import { SearchLanguage, SearchLanguages } from '@models/CountriesAndLanguages';
import FilterDefinition from '@models/FilterDefinition';
import { FilterField, FilterGroup } from '@models/SearchFilter';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { formatDate } from '@utils/dateUtils';
import { getNewFilterGroup } from '@utils/filterUtils';

type FilterDisplayProps = {
  filterGroup: FilterGroup;
  onChange: (filterGroup: FilterGroup) => void;
};

type FilterChipProps = {
  allFilters: FilterGroup;
  filterDefs: FilterDefinition[];
  filterField: FilterField;
  handleChange: (filterField: FilterField) => void;
  objPath: string[];
  parent?: string;
  setFilters: (filterGroup: FilterGroup) => void;
};

const recursiveDelete = (
  filterGroup: FilterGroup | undefined,
  objPath: string[]
): FilterGroup | undefined => {
  if (!filterGroup) return undefined;

  const curId = objPath.shift();

  const tmpFields = filterGroup.fields?.reduce((acc: FilterField[], cur) => {
    if (cur.id === curId) {
      if (objPath.length === 0) return acc;
      const filter = recursiveDelete(cur.filter, objPath);
      return !filter || !filter.fields || filter.fields.length === 0
        ? acc
        : [...acc, { ...cur, filter }];
    }

    return [...acc, cur];
  }, []);

  if (!tmpFields || tmpFields.length === 0) return undefined;

  return { ...filterGroup, fields: tmpFields };
};

const FilterChip: FC<FilterChipProps> = ({
  allFilters,
  filterField,
  filterDefs,
  handleChange,
  objPath,
  parent,
  setFilters,
}) => {
  const { t } = useTranslation();
  const { isReadonly } = useSearchAndFilter();

  const {
    filterDefLoad: { data: filterDefinitions },
  } = useSearchAndFilter();

  const filterDefinition = filterDefs.find(
    definition => definition.propertyName === filterField.propertyName
  );

  const handleOnChangeChild = (id: string) => (changedFilterField: FilterField) => {
    if (filterField.filter) {
      const fields = filterField.filter?.fields.map(f => (f.id !== id ? changedFilterField : f));
      if (fields && fields.length > 0)
        handleChange({ ...filterField, filter: { ...filterField.filter, fields } });
      else {
        const tmp = { ...filterField };
        delete tmp.filter;
        handleChange(tmp);
      }
    }
  };

  const hasSubFilters = filterField.filter && filterField.filter.fields.length > 0;

  if (!filterField.propertyName && hasSubFilters && filterDefinitions)
    return (
      <Fragment>
        {filterField.filter?.fields.map(f => (
          <FilterChip
            allFilters={allFilters}
            filterDefs={filterDefinitions}
            filterField={f}
            handleChange={handleOnChangeChild(f.id)}
            key={f.id}
            objPath={[...objPath, f.id]}
            setFilters={setFilters}
          />
        ))}
      </Fragment>
    );

  if (!filterDefinition) return null;

  if (filterField.value != null && filterField.value !== '') {
    const isDate =
      filterDefinition.type === 'dateTime' &&
      filterField.value &&
      typeof filterField.value !== 'boolean' &&
      dayjs(filterField.value).isValid();

    return (
      <Grid>
        <Chip
          label={
            <Fragment>
              {parent && SearchLanguages.includes(filterDefinition.propertyName as SearchLanguage)
                ? `${parent} (${filterDefinition.displayName})`
                : filterDefinition.displayName}
              {filterDefinition.type === 'boolean'
                ? ': '
                : ` ${t('oData.operator', { context: filterField.operator }).toLowerCase()} `}
              <strong>
                {isDate ? formatDate(String(filterField.value)) : filterField.value.toString()}
              </strong>
            </Fragment>
          }
          onDelete={
            isReadonly
              ? undefined
              : () => {
                  const changed = recursiveDelete(allFilters, objPath) ?? getNewFilterGroup(true);
                  setFilters(changed);
                }
          }
          size='small'
        />
      </Grid>
    );
  }

  if (hasSubFilters)
    return (
      <Fragment>
        {filterField.filter?.fields.map(f => (
          <FilterChip
            allFilters={allFilters}
            filterDefs={filterDefinition.fields}
            filterField={f}
            handleChange={handleOnChangeChild(f.id)}
            key={f.id}
            objPath={[...objPath, f.id]}
            parent={filterDefinition.displayName}
            setFilters={setFilters}
          />
        ))}
      </Fragment>
    );

  return null;
};

const FilterDisplay: FC<FilterDisplayProps> = ({ filterGroup, onChange }) => {
  const { t } = useTranslation();
  const { isReadonly } = useSearchAndFilter();

  const {
    filterDefLoad: { data: filterDefinitions },
  } = useSearchAndFilter();

  const handleChange = (id: string) => (filterField: FilterField) =>
    onChange({
      ...filterGroup,
      fields: filterGroup.fields?.map(field => (field.id === id ? filterField : field)),
    });

  if (!filterGroup || !filterDefinitions) return null;

  return (
    <Grid alignItems='center' container spacing={1}>
      {!isReadonly && (
        <Grid>
          <Chip
            color='primary'
            label={t('clear')}
            onClick={() => onChange(getNewFilterGroup(true))}
            size='small'
            title={t('clearFilters')}
          />
        </Grid>
      )}

      {filterGroup.fields?.map(filterField => (
        <FilterChip
          allFilters={filterGroup}
          filterDefs={filterDefinitions}
          filterField={filterField}
          handleChange={handleChange(filterField.id)}
          key={filterField.id || JSON.stringify(filterField)}
          objPath={[filterField.id]}
          setFilters={onChange}
        />
      ))}
    </Grid>
  );
};

export default FilterDisplay;
